import type { TreeItemContext } from '../types';

import { cn } from 'cn';
import { type MouseEvent, type PropsWithChildren, type ReactNode } from 'react';

import { smoothScrollTo } from '@vestigas/utils';

import { buildAnchorLink } from '../lib/anchors';

import { TreeAction } from './TreeAction';

type P = PropsWithChildren<{
  /** Context for building the anchor link */
  context?: TreeItemContext;
  /** The ID of the currently focused/visible element */
  focusElementID?: string;
  /** The nesting level (1 = root level) */
  level?: number;
  /** Replaces the default scroll-to-anchor behavior */
  onSelect?: (context: TreeItemContext) => void;
}> &
  ComponentStyling;

/**
 * Individual tree item with click-to-scroll functionality.
 * Displays active state when the corresponding form section is visible.
 * Hierarchy is expressed by indentation of nested groups. A string row
 * without a `context` is static text, not a link.
 */
export function TreeItem({
  children,
  className,
  context = {},
  focusElementID,
  level = 1,
  onSelect,
  style,
}: P) {
  let content = children;
  const anchorId = buildAnchorLink(Object.values(context));
  const href = anchorId ? `#${anchorId}` : undefined;

  function handleClick(event: MouseEvent<HTMLAnchorElement>) {
    event.preventDefault();

    if (onSelect) {
      onSelect(context);
      return;
    }

    const element = globalThis.document.getElementById(anchorId);
    if (element) {
      smoothScrollTo(element, 0);
    }
  }

  const isActive = Boolean(
    focusElementID && anchorId && focusElementID === anchorId,
  );

  const renderTitle = (title: ReactNode) =>
    anchorId ? (
      <TreeAction href={href} isActive={isActive} onClick={handleClick}>
        {title}
      </TreeAction>
    ) : (
      <span className="block truncate">{title}</span>
    );

  // Wrap string children in TreeAction
  if (typeof children === 'string') {
    content = renderTitle(content);
  } else if (Array.isArray(content)) {
    // If children is an array, first element is the title, rest are nested items
    const title = content.slice(0, 1)[0];

    content = (
      <>
        {renderTitle(title)}
        {content.slice(1)}
      </>
    );
  }

  return (
    <li
      className={cn(
        'w-full whitespace-nowrap',
        {
          'font-medium text-tiga-text-default': isActive,
          'mb-4 pl-4 text-base': level === 1,
          'pt-3 text-sm': level > 1,
          'text-tiga-text-lighter': !isActive,
        },
        className,
      )}
      style={style}
    >
      {content}
    </li>
  );
}
